import { type ResultSet, Filter } from '@cubejs-client/core'
import { isValidCubeFilter } from 'cube'
import { mapKeys } from 'lodash'
import { equals } from 'ramda'

import { TypeFormats } from '@driftt/dds.reports'
import { tracking } from '@driftt/traffic-control'

/**
 *
 * Translates a list of records to "split out" the Cube schema from the dimension key. This is useful to formatting Cube result sets for tables.
 *
 * @param dataObjects
 * @returns Record<string, string | number | boolean | null>[]
 */
const toRenderableColumnKey = (
  dataObjects: Record<string, string | number | boolean | null>[]
) => {
  return dataObjects.map(
    (dataObj: Record<string, string | number | boolean | null>) =>
      mapKeys(dataObj, (value, key) => key.split('.')[1])
  )
}

/**
 *
 * Translates a Cube result set to renderable table data.
 *
 * @param resultSet
 * @returns Record<string, string | number | boolean | null>[]
 */
export const resultSetToTableData = (resultSet?: ResultSet) => {
  const tableResults = resultSet ? resultSet.tablePivot() : []
  return toRenderableColumnKey(tableResults)
}

/**
 *
 * Translates a Cube result set to renderable select options.
 *
 * @param resultSet
 * @returns {{label: string, value: string[]}}
 */
export const resultSetToOptionsList = (
  labelDimension: string,
  valueDimension: string,
  resultSet?: ResultSet
) => {
  const tableResults = resultSet ? resultSet.tablePivot() : []
  return tableResults.map(
    (dataObj: Record<string, string | number | boolean | null>) => {
      return {
        label: dataObj[labelDimension]?.toString() ?? '',
        value: dataObj[valueDimension]?.toString() ?? '',
      }
    }
  )
}

export const formatTextWithUnderscores = (text: string) =>
  TypeFormats.TITLE_CASE(text)?.replace(/\s+/g, '_')

export const formatExportDateRange = (startDate: Date, endDate: Date) => {
  const datesAreEqual = !!equals(
    TypeFormats.DATE(startDate),
    TypeFormats.DATE(endDate)
  )

  if (datesAreEqual) {
    return `${TypeFormats.DATE(startDate)}`
  } else {
    return `${TypeFormats.DATE(startDate)}_to_${TypeFormats.DATE(endDate)}`
  }
}

export const trackDrillDownExportClick = (
  metricType: string,
  startDate: Date,
  endDate: Date,
  totalCount: number,
  pageName: string = window.location.pathname
) =>
  tracking.trackEvent('CUBE_DRILL_DOWN_EXPORT_CLICK', {
    metricType,
    startDate,
    endDate,
    totalCount,
    pageName,
  })

export const formatExportFileName = (
  title: string,
  startDate: Date,
  endDate: Date
) =>
  `Drift_${formatTextWithUnderscores(title)}_${formatExportDateRange(
    startDate,
    endDate
  )}`

/**
 *
 * Returns supported filters - Binary and Unary filter types
 *
 * TODO: Drop unsupported filter members
 *
 * @param {Filter[]} filters Filters to sanitize
 * @returns {Filter[]} Supported cube filters
 */
export const sanitizedCubeFilters = (filters: Filter[]): Filter[] => {
  return filters.filter(isValidCubeFilter)
}
